// Instrumentation légère pour mesurer la durée réelle des écrans d'attente
// (verdict court, mémo). Objectif : itérer sur la dramaturgie sans deviner.
//
// - logWaitStart(scope, meta?)   → au mount du loader
// - logWaitLongShown(scope, sec) → quand le message "plus de temps que prévu" apparaît
// - logWaitEnd(scope, outcome)   → quand le 200 arrive (ou abandon/unmount)
//
// Tout est journalisé en `console.info` (préfixe [hojin-wait]), persisté dans
// sessionStorage (`hojin.wait.timeline`, capé à 50 entrées) et émis via
// `window` (`CustomEvent('hojin:wait')`) pour brancher une couche analytics
// plus tard sans toucher aux composants.

export type WaitScope = "memo" | "verdict-short";
export type WaitOutcome = "ready" | "error" | "abandoned";

interface WaitEvent {
  scope: WaitScope;
  phase: "start" | "long_shown" | "end";
  ts: number;
  durationMs?: number;
  outcome?: WaitOutcome;
  meta?: Record<string, unknown>;
}

const STORAGE_KEY = "hojin.wait.timeline";
const MAX_ENTRIES = 50;
const starts = new Map<string, number>();

function persist(ev: WaitEvent) {
  try {
    if (typeof sessionStorage === "undefined") return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const arr: WaitEvent[] = raw ? JSON.parse(raw) : [];
    arr.push(ev);
    if (arr.length > MAX_ENTRIES) arr.splice(0, arr.length - MAX_ENTRIES);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch {
    /* ignore quota / parsing */
  }
}

function emit(ev: WaitEvent) {
  // eslint-disable-next-line no-console
  console.info("[hojin-wait]", ev);
  persist(ev);
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("hojin:wait", { detail: ev }));
    } catch {
      /* ignore */
    }
  }
}

function key(scope: WaitScope, id?: string) {
  return id ? `${scope}:${id}` : scope;
}

export function logWaitStart(
  scope: WaitScope,
  meta?: Record<string, unknown> & { id?: string },
) {
  const k = key(scope, meta?.id as string | undefined);
  starts.set(k, performance.now());
  emit({ scope, phase: "start", ts: Date.now(), meta });
}

export function logWaitLongShown(
  scope: WaitScope,
  elapsedSec: number,
  meta?: Record<string, unknown> & { id?: string },
) {
  emit({
    scope,
    phase: "long_shown",
    ts: Date.now(),
    durationMs: Math.round(elapsedSec * 1000),
    meta,
  });
}

export function logWaitEnd(
  scope: WaitScope,
  outcome: WaitOutcome,
  meta?: Record<string, unknown> & { id?: string },
) {
  const k = key(scope, meta?.id as string | undefined);
  const startedAt = starts.get(k);
  const durationMs = startedAt ? Math.round(performance.now() - startedAt) : undefined;
  starts.delete(k);
  emit({ scope, phase: "end", ts: Date.now(), durationMs, outcome, meta });
}

export function readWaitTimeline(): WaitEvent[] {
  try {
    if (typeof sessionStorage === "undefined") return [];
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as WaitEvent[]) : [];
  } catch {
    return [];
  }
}
